import { Link } from "react-router-dom";
import { Play, Plus, Check, Info } from "lucide-react";
import { isInMyList, toggleMyList } from "../lib/localStore";
import { mediaUrl } from "../lib/media";
import { useState, useEffect, useRef } from "react";

export default function HeroSection({ series }) {
    const [saved, setSaved] = useState(false);
    const videoRef = useRef(null);

    useEffect(() => {
        if (series) setSaved(isInMyList(series.id, "series"));
    }, [series]);

    if (!series) return null;

    const totalEpisodes = (series.episodes || []).filter(e => !e.is_coming_soon).length;
    const hasComingSoon = (series.episodes || []).some(e => e.is_coming_soon);

    return (
        <section data-testid="hero-section" className="relative w-full min-h-[92vh] overflow-hidden">
            {/* Background: trailer video if available, else backdrop image */}
            <div className="absolute inset-0">
                {series.trailer_url ? (
                    <video
                        ref={videoRef}
                        src={mediaUrl(series.trailer_url)}
                        poster={mediaUrl(series.backdrop)}
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <img src={mediaUrl(series.backdrop)} alt={series.title} className="w-full h-full object-cover" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B]/70 to-[#0B0B0B]/40" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0B] via-[#0B0B0B]/60 to-transparent" />
            </div>

            <div className="relative z-10 mx-auto max-w-[1600px] px-6 lg:px-10 pt-40 pb-24 flex flex-col justify-end min-h-[92vh]">
                <div className="max-w-2xl animate-fade-up">
                    <div className="flex items-center gap-2 mb-4">
                        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#FFD400]">
                            Apni Dukan Original
                        </span>
                        <span className="w-1 h-1 rounded-full bg-white/40" />
                        <span className="text-[11px] uppercase tracking-widest text-white/70">Series</span>
                    </div>
                    <h1 className="font-display font-black text-6xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tight title-glow">
                        <span className="text-white">BLACK</span><span className="text-[#B4131C]">LIST</span>
                    </h1>
                    <p className="mt-4 text-lg sm:text-xl font-medium text-white/85 title-glow">
                        {series.genre === "Crime Thriller" ? "An AI Cinematic Crime Thriller" : series.genre}
                    </p>
                    <p className="mt-4 text-sm sm:text-base text-white/70 leading-relaxed max-w-xl line-clamp-3">
                        {series.description}
                    </p>

                    <div className="mt-6 flex items-center gap-3">
                        <Link
                            to={`/series/${series.id}`}
                            data-testid="hero-watch-now-btn"
                            className="inline-flex items-center gap-2 bg-[#FFD400] text-black font-bold rounded-full px-7 py-3.5 hover:scale-105 transition-transform duration-200 shadow-[0_10px_40px_-10px_rgba(255,212,0,0.6)]"
                        >
                            <Play className="w-5 h-5 fill-black" />
                            Watch Now
                        </Link>
                        <button
                            data-testid="hero-my-list-btn"
                            onClick={() => {
                                toggleMyList({ id: series.id, type: "series", title: series.title, thumbnail: series.poster });
                                setSaved(isInMyList(series.id, "series"));
                            }}
                            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md text-white rounded-full px-7 py-3.5 border border-white/20 hover:bg-white/20 transition-colors"
                        >
                            {saved ? <Check className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                            {saved ? "In My List" : "My List"}
                        </button>
                        <Link
                            to={`/series/${series.id}`}
                            data-testid="hero-info-btn"
                            className="hidden sm:inline-flex items-center gap-2 bg-transparent text-white/80 rounded-full px-5 py-3.5 hover:text-[#FFD400] transition-colors"
                        >
                            <Info className="w-5 h-5" />
                            More Info
                        </Link>
                    </div>

                    <div className="mt-8 flex items-center gap-6 text-sm">
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-[#FFD400] animate-pulse" />
                            <span className="text-white/90 font-semibold" data-testid="hero-episodes-released">
                                {totalEpisodes} Episodes Released
                            </span>
                        </div>
                        {hasComingSoon && (
                            <span className="text-[11px] font-bold uppercase tracking-wider bg-[#FFD400] text-black px-3 py-1.5 rounded" data-testid="hero-coming-soon-badge">
                                Part 4 Coming Soon
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
